import { ColumnDef } from "@tanstack/react-table";
import { Business } from "@norma-bi/bi-api";
import { BaseSettingsTable } from "../components/BaseSettingsTable";
import { BaseSettingsCell } from "../components/BaseSettingsCell";
import React from "react";
import { RenameDialog } from "./RenameDialog";
import { useUpdateBusiness } from "@/hooks/useUpdateBusiness";
import { customToast } from "@/utils/customToast";
import { AddBusinessDialog } from "./AddBusinessDialog";
import { useAuth } from "@/Auth";

export type SettingsBusinessesTableProps = {
  businesses: Business[];
  tableClassName?: string;
};

export function SettingsBusinessesTable({
  businesses,
  tableClassName = "settings-table",
}: SettingsBusinessesTableProps) {
  const [selectedBusiness, setSelectedBusiness] = React.useState<Business | null>(null);
  const [showRenameDialog, setShowRenameDialog] = React.useState(false);
  const [showAddBusinessDialog, setShowAddBusinessDialog] = React.useState(false);

  const updateBusiness = useUpdateBusiness();
  const isDemo = useAuth().user?.is_demo ?? false;

  const columns: ColumnDef<Business, keyof Business>[] = [
    {
      accessorKey: "display_name",
      header: "Display Name",
      cell: ({ getValue }) => BaseSettingsCell.avatar(getValue()),
    },
    {
      accessorKey: "name",
      header: "Business Name",
    },
    {
      accessorKey: "venues_count",
      header: "Venues",
    },
    {
      accessorKey: "users_count",
      header: "Users",
    },
  ];

  const TopSection = (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="text-xl font-medium text-feintGray">Businesses</div>
        {!isDemo && (
          <button
            type="button"
            className="rounded-lg bg-primaryColor px-4 py-2 text-sm text-white"
            onClick={() => setShowAddBusinessDialog(true)}
          >
            Add Business
          </button>
        )}
      </div>
    </div>
  );

  const handleRowClick = (business: Business) => {
    setSelectedBusiness(business);
    setShowRenameDialog(true);
  };

  const handleRename = (newDisplayName: string) => {
    if (selectedBusiness) {
      updateBusiness.mutate(
        { businessId: selectedBusiness.id, changeset: { display_name: newDisplayName } },
        {
          onSuccess: () => {
            customToast("Successfully Changed Name of Business", "success", "bottom");
            setShowRenameDialog(false);
            setSelectedBusiness(null);
          },
          onError: (error) => {
            console.error("Error updating business name:", error);
            customToast("Failed to rename business", "error", "bottom");
          },
        },
      );
    }
  };

  return (
    <>
      <BaseSettingsTable
        data={businesses}
        columns={columns}
        tableClassName={tableClassName}
        TopSection={TopSection}
        onRowClick={isDemo ? undefined : handleRowClick}
      />

      {selectedBusiness && showRenameDialog && (
        <RenameDialog
          show={showRenameDialog}
          setShow={setShowRenameDialog}
          business={selectedBusiness}
          onConfirm={handleRename}
        />
      )}

      {showAddBusinessDialog && (
        <AddBusinessDialog onClose={() => setShowAddBusinessDialog(false)} />
      )}
    </>
  );
}
